import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle } from "lucide-react";

const volunteerKeywords = [
  "Volunteer for NGO",
  "NGO Volunteer India",
  "Sevaa Foundation Volunteer",
  "Social Work Volunteer",
  "Charity Volunteer",
  "Education Volunteer",
  "Healthcare Camp Volunteer",
  "Food Donation Volunteer",
  "Women Empowerment Volunteer",
  "CSR Volunteer",
  "Samaj Seva",
  "स्वयंसेवक बनें",
  "समाज सेवा",
  "गरीब बच्चों की मदद",
  "बच्चों की पढ़ाई",
  "भोजन वितरण",
  "स्वास्थ्य सेवा",
  "महिला सशक्तिकरण",
];

const volunteerRoles = [
  "Field Volunteer (Drives)",
  "Education & Teaching",
  "Content & Social Media",
  "Event Planning",
];

const trustStats = [
  { num: "10+", label: "Active Cities", hindi: "सक्रिय शहर" },
  { num: "5,000+", label: "Volunteers", hindi: "स्वयंसेवक" },
  { num: "50+", label: "Weekly Drives", hindi: "साप्ताहिक अभियान" },
  { num: "1M+", label: "Lives Touched", hindi: "लोगों तक सहायता" },
];

const Volunteer = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    emailAddress: "",
    interestedRole: "",
    availability: "Weekends Only",
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://www.sevaafoundation.com/seva-backend/api/volunteer/submit.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setIsFormOpen(false);
        setShowToast(true);

        setFormData({
          fullName: "",
          phoneNumber: "",
          emailAddress: "",
          interestedRole: "",
          availability: "Weekends Only",
        });

        setTimeout(() => {
          setShowToast(false);
        }, 2000);
      } else {
        alert(`Error: ${result.message || "Failed to submit application."}`);
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("Something went wrong. Please check if the server is running.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="volunteer"
      aria-labelledby="volunteer-heading"
      className="relative py-24 lg:py-32 font-poppins overflow-hidden bg-cover bg-center bg-fixed"
      style={{
        backgroundImage:
          "url('https://img.freepik.com/premium-photo/many-poor-mexican-children-reached-out-beg-food_1057402-2911.jpg?semt=ais_hybrid&w=740&q=80')",
      }}
      role="img"
      aria-label="Background image showing children reaching out, representing the need for NGO volunteers"
    >
      {/* SUCCESS TOAST POPUP */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: -50, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -50, x: "-50%" }}
            role="status"
            aria-live="polite"
            className="fixed top-10 left-1/2 -translate-x-1/2 z-[200] bg-[#0f1716]/95 backdrop-blur-sm border border-emerald-500/30 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 min-w-[320px] max-w-md w-[90vw]"
          >
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
              <CheckCircle className="text-emerald-400" size={24} aria-hidden="true" />
            </div>

            <div>
              <h4 className="text-white font-bold text-sm">
                Thank You for Submitting!
              </h4>
              <p className="text-teal-50/70 text-xs mt-1">
                We&apos;ve received your request. We&apos;ll contact you soon.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute inset-0 bg-gradient-to-r from-[#0f1716]/95 via-[#0f766e]/90 to-[#0f1716]/95" aria-hidden="true"></div>

      <div
        className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none"
        aria-hidden="true"
      ></div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 text-center flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-8 shadow-xl"
        >
          <span className="w-2 h-2 bg-[#ffb000] rounded-full animate-pulse" aria-hidden="true"></span>
          <span className="text-[#ffb000] text-[10px] md:text-xs font-black uppercase tracking-[0.2em]">
            Join Ground Operations
          </span>
        </motion.div>

        <motion.h2
          id="volunteer-heading"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight mb-6"
        >
          Be the Reason Someone <br className="hidden md:block" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ffb000] to-orange-400">
            Smiles Today.
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-teal-50 text-base md:text-xl font-medium max-w-3xl mx-auto mb-6 leading-relaxed opacity-90"
        >
          Become a volunteer with Sevaa Foundation and support child education,
          healthcare camps, hunger relief, women empowerment, food donation
          drives and social welfare work across India.
        </motion.p>

        <p className="text-teal-50/80 text-sm md:text-base font-medium max-w-2xl mx-auto mb-12 leading-relaxed">
          We are a family of 5,000+ passionate volunteers working on the ground.
          Whether you have a few hours or a whole weekend, your time is the
          greatest donation.
        </p>

        <p className="sr-only">
          Sevaa Foundation volunteer program, NGO volunteer India, samaj seva,
          seva karya, charity volunteer, education volunteer, healthcare
          volunteer, bhojan vitran volunteer, mahila sashaktikaran volunteer,
          garib bachchon ki madad, bachchon ki padhai, समाज सेवा, स्वयंसेवक
          बनें.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto"
        >
          <button
            onClick={() => setIsFormOpen(true)}
            className="bg-[#ffb000] text-black font-black text-xs md:text-sm uppercase tracking-widest px-10 py-5 rounded-2xl shadow-[0_0_40px_rgba(255,176,0,0.3)] hover:bg-white hover:shadow-[0_0_40px_rgba(255,255,255,0.4)] hover:-translate-y-1 transition-all duration-300"
            aria-label="Register as a volunteer with Sevaa Foundation NGO"
          >
            Register as Volunteer
          </button>

          <button
            onClick={() => (window.location.href = "/csr")}
            className="bg-transparent text-white border-2 border-white/30 font-black text-xs md:text-sm uppercase tracking-widest px-10 py-5 rounded-2xl hover:bg-white/10 hover:border-white transition-all duration-300"
            aria-label="Explore volunteer and CSR roles at Sevaa Foundation"
          >
            Explore Roles
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 border-t border-white/10 pt-12 w-full max-w-4xl"
        >
          {trustStats.map((stat) => (
            <article key={stat.label} className="text-center">
              <h3 className="text-4xl md:text-5xl font-black text-white mb-2">
                {stat.num}
              </h3>
              <p className="text-[10px] md:text-xs uppercase tracking-widest text-[#ffb000] font-bold">
                {stat.label}
              </p>
              <p className="text-[10px] md:text-xs text-teal-50/70 mt-1 font-semibold">
                {stat.hindi}
              </p>
            </article>
          ))}
        </motion.div>

        {/* <div className="mt-12 flex flex-wrap justify-center gap-2 max-w-5xl" aria-label="Volunteer initiatives covered by our NGO">
          {volunteerKeywords.map((keyword) => (
            <span
              key={keyword}
              className="rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-[10px] md:text-xs font-bold text-teal-50/90 backdrop-blur-sm"
            >
              {keyword}
            </span>
          ))}
        </div> */}
      </div>

      <AnimatePresence>
        {isFormOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="volunteer-form-title"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsFormOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
              aria-hidden="true"
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative bg-black/20 backdrop-blur-xl border border-white/10 w-[90vw] max-w-lg p-6 md:p-8 rounded-3xl shadow-2xl overflow-hidden"
            >
              <button
                onClick={() => setIsFormOpen(false)}
                className="absolute top-5 right-5 text-white/50 hover:text-white transition-colors"
                aria-label="Close volunteer application form"
              >
                ✕
              </button>

              <h3
                id="volunteer-form-title"
                className="text-2xl font-black text-white mb-1"
              >
                Volunteer Application
              </h3>

              <p className="text-teal-50/60 mb-5 text-xs">
                Join our mission and make a real-world impact through education,
                healthcare, charity drives and social welfare work.
              </p>

              <form className="space-y-3.5 text-left" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  <div className="space-y-1.5">
                    <label
                      htmlFor="fullName"
                      className="text-[9px] uppercase tracking-widest font-bold text-[#ffb000]"
                    >
                      Full Name
                    </label>
                    <input
                      id="fullName"
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#ffb000] transition-colors"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label
                      htmlFor="phoneNumber"
                      className="text-[9px] uppercase tracking-widest font-bold text-[#ffb000]"
                    >
                      Phone Number
                    </label>
                    <input
                      id="phoneNumber"
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="+91 00000 00000"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#ffb000] transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="emailAddress"
                    className="text-[9px] uppercase tracking-widest font-bold text-[#ffb000]"
                  >
                    Email Address
                  </label>
                  <input
                    id="emailAddress"
                    type="email"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#ffb000] transition-colors"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="interestedRole"
                    className="text-[9px] uppercase tracking-widest font-bold text-[#ffb000]"
                  >
                    Interested Role
                  </label>
                  <select
                    id="interestedRole"
                    name="interestedRole"
                    value={formData.interestedRole}
                    onChange={handleInputChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#ffb000] transition-colors appearance-none"
                    required
                  >
                    <option value="" disabled className="bg-[#0f1716] text-white">
                      Select a Role...
                    </option>

                    {volunteerRoles.map((role) => (
                      <option
                        key={role}
                        value={role}
                        className="bg-[#0f1716] text-white"
                      >
                        {role}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="availability"
                    className="text-[9px] uppercase tracking-widest font-bold text-[#ffb000]"
                  >
                    Availability
                  </label>
                  <select
                    id="availability"
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#ffb000] transition-colors appearance-none"
                    required
                  >
                    <option value="Weekends Only" className="bg-[#0f1716] text-white">
                      Weekends Only
                    </option>
                    <option value="Weekdays Only" className="bg-[#0f1716] text-white">
                      Weekdays Only
                    </option>
                    <option value="Flexible" className="bg-[#0f1716] text-white">
                      Flexible
                    </option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#ffb000] text-black font-black text-sm uppercase tracking-widest py-4 rounded-xl mt-4 hover:bg-white hover:-translate-y-1 transition-all duration-300 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Volunteer;